import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DatabaseZap, PackagePlus, Loader2, CheckCircle2, ShieldAlert, ClipboardCheck } from "lucide-react";

// Big BOSS'a özel tek-tık: önizlemeden export edilen ürün tohum dosyasından CANLI veritabanına
// yalnız EKSİK ürünleri (SKU bazlı) ekler. Var olanları atlar, hiçbir kaydı silmez/günceller.
export default function ProductMigrationCard() {
  const { user } = useAuth();
  const [withCats, setWithCats] = useState(true);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // dry-run sonucu
  const [confirm, setConfirm] = useState(false);
  const [done, setDone] = useState(null);

  if (!user?.is_owner) return null;

  const run = async (commit) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/system/migrate-products?commit=${commit}&with_categories=${withCats}`,
      );
      if (commit) {
        setDone(data);
        setPreview(null);
        setConfirm(false);
        const added = (data.collections || []).reduce((s, c) => s + (c.added || 0), 0);
        toast.success(added > 0 ? `${added} eksik kayıt canlıya eklendi` : "Eklenecek eksik kayıt yoktu — canlı zaten güncel");
      } else {
        setPreview(data);
        setDone(null);
        setConfirm(false);
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  const rows = (r) => (r?.collections || []);
  const wouldAdd = rows(preview).reduce((s, c) => s + (c.would_add || 0), 0);

  const Table = ({ data, field, label }) => (
    <div className="mt-3 overflow-hidden rounded-lg ring-1 ring-inset ring-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
          <tr><th className="px-3 py-2">Koleksiyon</th><th className="px-3 py-2 text-right">Kaynak</th><th className="px-3 py-2 text-right">Canlıda mevcut</th><th className="px-3 py-2 text-right">{label}</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows(data).map((c) => (
            <tr key={c.collection} data-testid={`mig-row-${c.collection}`}>
              <td className="px-3 py-2 font-medium text-slate-700">{c.collection === "products" ? "Ürünler" : "Kategoriler"}</td>
              <td className="px-3 py-2 text-right tabular text-slate-500">{c.source ?? "—"}</td>
              <td className="px-3 py-2 text-right tabular text-slate-500">{c.existing ?? "—"}</td>
              <td className={`px-3 py-2 text-right tabular font-bold ${(c[field] || 0) > 0 ? "text-emerald-700" : "text-slate-400"}`}>{c[field] ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="rounded-2xl bg-white p-5 ring-1 ring-inset ring-slate-200" data-testid="product-migration-card">
      <div className="flex items-center gap-2.5">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-brand-green"><DatabaseZap className="h-5 w-5" /></div>
        <div>
          <p className="overline">Veri Aktarımı</p>
          <h2 className="font-heading text-lg font-bold text-slate-900">Eksik Ürünleri Canlıya Aktar</h2>
        </div>
      </div>
      <p className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700 ring-1 ring-inset ring-emerald-200">
        Önizleme kataloğundaki ürünlerden canlıda <b>bulunmayanları</b> (SKU bazlı) ekler. Zaten var olan ürünler atlanır; hiçbir kayıt silinmez veya değiştirilmez. Önce <b>Kontrol Et</b> ile kaç ürün ekleneceğini görün, sonra <b>Aktar</b> deyin.
      </p>

      <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" data-testid="mig-with-categories" checked={withCats} onChange={(e) => setWithCats(e.target.checked)} className="h-4 w-4 accent-brand-green" />
        Eksik kategorileri de aktar
      </label>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button data-testid="mig-dryrun-btn" variant="outline" disabled={busy} onClick={() => run(false)}
          className="border-slate-300">
          {busy && !confirm ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardCheck className="mr-2 h-4 w-4" />} Kontrol Et (Dry-run)
        </Button>
      </div>

      {preview && (
        <div data-testid="mig-preview" className="mt-4">
          <Table data={preview} field="would_add" label="Eklenecek" />
          {wouldAdd > 0 ? (
            !confirm ? (
              <Button data-testid="mig-commit-btn" disabled={busy} onClick={() => setConfirm(true)}
                className="mt-3 bg-brand-green text-white hover:bg-emerald-600">
                <PackagePlus className="mr-2 h-4 w-4" /> {wouldAdd} eksik kaydı canlıya aktar
              </Button>
            ) : (
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 ring-1 ring-inset ring-amber-200">
                <ShieldAlert className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">Emin misiniz? {wouldAdd} kayıt canlıya eklenecek.</span>
                <div className="ml-auto flex gap-2">
                  <Button data-testid="mig-commit-confirm" size="sm" disabled={busy} onClick={() => run(true)}
                    className="bg-brand-green text-white hover:bg-emerald-600">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Evet, Aktar"}</Button>
                  <Button data-testid="mig-commit-cancel" size="sm" variant="outline" disabled={busy} onClick={() => setConfirm(false)}>İptal</Button>
                </div>
              </div>
            )
          ) : (
            <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Eklenecek eksik kayıt yok — canlı zaten güncel.</p>
          )}
        </div>
      )}

      {done && (
        <div data-testid="mig-done" className="mt-4">
          <Table data={done} field="added" label="Eklendi" />
          <p className="mt-3 flex items-center gap-1.5 text-sm font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Aktarım tamamlandı.</p>
        </div>
      )}
    </div>
  );
}
